import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useGetOwnPhoneNumbersQuery, useRefreshA2pStatusMutation } from "@/store/rest_api";
import A2pRegistration from "@/model/interface/A2pRegistration";
import { toast } from "sonner";
import { Loader2, RefreshCw, Phone } from "lucide-react";

interface Props {
  registration: A2pRegistration;
}

export default function PhoneNumbersStep({ registration }: Props) {
  const { data: allPhoneNumbers = [] } = useGetOwnPhoneNumbersQuery();
  const phoneNumbers = allPhoneNumbers.filter((pn) => pn.type !== "TOLL_FREE");
  const [refreshStatus, { isLoading: isRefreshing }] = useRefreshA2pStatusMutation();

  const handleRefresh = async () => {
    try {
      await refreshStatus().unwrap();
      toast.success("Status refreshed");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to refresh status");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Phone Numbers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {registration.messagingServiceSid ? (
          <>
            <div className="text-sm text-muted-foreground">
              Messaging Service: <code className="text-xs">{registration.messagingServiceSid}</code>
            </div>
            <div className="space-y-2">
              {phoneNumbers.map((pn) => {
                const alreadyEnrolled =
                  pn.a2pEnrollmentStatus === "ENROLLED" || pn.a2pEnrollmentStatus === "OVERRIDE";
                return (
                  <div
                    key={pn.id}
                    className={`flex items-center justify-between border rounded-lg p-3 ${
                      alreadyEnrolled ? "opacity-60" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium text-sm">{pn.alias || pn.number}</div>
                        {pn.alias && <div className="text-xs text-muted-foreground">{pn.number}</div>}
                      </div>
                    </div>
                    {pn.a2pEnrollmentStatus === "OVERRIDE" ? (
                      <Badge variant="outline">Override</Badge>
                    ) : pn.a2pEnrollmentStatus === "ENROLLED" ? (
                      <Badge variant="default">Enrolled</Badge>
                    ) : (
                      <Badge variant="secondary">Not enrolled</Badge>
                    )}
                  </div>
                );
              })}
              {phoneNumbers.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No phone numbers found. Purchase phone numbers first.
                </div>
              )}
            </div>
          </>
        ) : (
          <Alert>
            <AlertDescription>
              The messaging service will be created automatically when you submit your campaign.
              All your active phone numbers will be added to the service.
            </AlertDescription>
          </Alert>
        )}

        {registration.campaignStatus && (
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold">Campaign Status:</span>
            <Badge variant={registration.campaignStatus === "VERIFIED" ? "default" : "secondary"}>
              {registration.campaignStatus}
            </Badge>
          </div>
        )}

        {registration.rejectionReason && (
          <Alert variant="destructive">
            <AlertDescription>{registration.rejectionReason}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
          {isRefreshing ? <Loader2 className="animate-spin mr-1 h-4 w-4" /> : <RefreshCw className="mr-1 h-4 w-4" />}
          Refresh Status
        </Button>
      </CardFooter>
    </Card>
  );
}
